/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { GetterTree } from 'vuex';

import questionTypes, { QuestionTypeOption } from '@/pages/survey_builder/questionTypes';
import { ApiSurveyDataPreview } from '@/pages/survey_builder/types';

import * as helpers from './helpers';
import { State, Question, QuestionOption, QuestionCondition } from './state';

export type Getters = { [P in keyof GettersDef]: ReturnType<GettersDef[P]> };

class GettersDef {
  isLoaded(state: State) {
    return state.isLoaded;
  }
  isSaving(state: State) {
    return state.isSaving;
  }
  canSave(state: State) {
    return !state.isSaving && state.isValid;
  }
  isDirty(state: State) {
    return state.isDirty;
  }
  canReset(state: State) {
    return state.isDirty;
  }
  isPreviewVisible(state: State) {
    return state.isPreviewVisible;
  }

  surveyId(state: State) {
    return state.surveyId;
  }
  title(state: State) {
    return state.title;
  }
  isTitleValid(state: State) {
    return helpers.isSurveyTitleValid(state.title);
  }
  isWeighted(state: State) {
    return state.isWeighted;
  }
  hasRiskThresholds(state: State) {
    return helpers.hasRiskThresholds(state);
  }
  midRiskThreshold(state: State) {
    return state.midRiskThreshold;
  }
  isMidRiskThresholdValid(state: State) {
    return helpers.isMidRiskThresholdValid(state.midRiskThreshold, state.highRiskThreshold);
  }
  highRiskThreshold(state: State) {
    return state.highRiskThreshold;
  }
  isHighRiskThresholdValid(state: State) {
    return helpers.isHighRiskThresholdValid(state.highRiskThreshold, state.midRiskThreshold);
  }
  minScore(state: State): number {
    return helpers.getMinSurveyScore(state);
  }
  maxScore(state: State): number {
    return helpers.getMaxSurveyScore(state);
  }
  questions(state: State) {
    return state.questions;
  }
  questionsCount(state: State) {
    return state.questions.length;
  }
  currentQuestions(state: State) {
    return state.questions.filter(question => question.pageNumber === state.currentPage);
  }

  currentPage(state: State) {
    return state.currentPage;
  }
  totalPages(state: State) {
    return state.totalPages;
  }
  pages(state: State) {
    return state.pages;
  }
  hasPreviousPage(state: State) {
    return state.currentPage > 1;
  }
  hasNextPage(state: State) {
    return state.currentPage < state.totalPages;
  }

  activeQuestionId(state: State) {
    return state.activeQuestionId;
  }
  questionsForConditions(_: State, getters: Getters): Question[] {
    return helpers.questionsForConditions(getters.questions);
  }

  editQuestion(state: State) {
    return helpers.findEditQuestion(state);
  }
  isEditingQuestion(_: State, getters: Getters): boolean {
    return getters.editQuestion != null;
  }
  editQuestionHasOptions(_: State, getters: Getters): boolean {
    return getters.editQuestion ? helpers.questionHasOptions(getters.editQuestion) : false;
  }

  editQuestionHasWeight(_: State, getters: Getters): boolean {
    return getters.isWeighted && (getters.editQuestion ? helpers.questionHasWeight(getters.editQuestion) : false);
  }
  editQuestionIsVisibleAlways(_: State, getters: Getters): boolean {
    return getters.editQuestion != null ? !helpers.questionHasConditions(getters.editQuestion) : false;
  }
  editQuestionIsVisibleConditional(_: State, getters: Getters): boolean {
    return getters.editQuestion != null ? helpers.questionHasConditions(getters.editQuestion) : false;
  }
  editQuestionConditionQuestions(_: State, getters: Getters): Question[] {
    if (!getters.editQuestion) {
      return [];
    }
    // TODO prevent cycles
    return getters.questionsForConditions.filter(question => question.id !== getters.editQuestion!.id);
  }
  isEditQuestionTypeValid(_: State, getters: Getters): boolean {
    return getters.editQuestion != null ? helpers.isQuestionTypeValid(getters.editQuestion) : false;
  }
  isEditQuestionTitleValid(_: State, getters: Getters): boolean {
    return getters.editQuestion != null ? helpers.isQuestionTitleValid(getters.editQuestion) : false;
  }
  isEditQuestionWeightValid(_: State, getters: Getters): boolean {
    return getters.editQuestion != null
      ? helpers.isQuestionWeightValid(getters.editQuestion, getters.isWeighted)
      : false;
  }
  isEditQuestionOptionsValid(_: State, getters: Getters): boolean {
    return getters.editQuestion != null
      ? helpers.isQuestionOptionsValid(getters.editQuestion, getters.isWeighted)
      : false;
  }
  isEditQuestionOptionTitleValid(_: State, getters: Getters): (option: QuestionOption) => boolean {
    if (getters.editQuestion == null) {
      return () => false;
    }
    return option => helpers.isQuestionOptionTitleValid(option);
  }
  isEditQuestionOptionMultiplierValid(_: State, getters: Getters): (option: QuestionOption) => boolean {
    if (getters.editQuestion == null) {
      return () => false;
    }
    const isWeighted = getters.isWeighted;
    return option => helpers.isQuestionOptionMultiplierValid(option, isWeighted);
  }
  questionsMap(state: State): Map<number, Question> {
    return helpers.getQuestionsMap(state.questions);
  }
  isEditQuestionConditionsValid(_: State, getters: Getters): boolean {
    return getters.editQuestion != null
      ? helpers.isQuestionConditionsValid(getters.questionsMap, getters.editQuestion)
      : false;
  }
  isEditQuestionConditionQuestionValid(_: State, getters: Getters): (condition: QuestionCondition) => boolean {
    const question = getters.editQuestion;
    if (question == null) {
      return () => false;
    }
    const questionsMap = getters.questionsMap;
    return condition => helpers.isQuestionConditionQuestionValid(questionsMap, question!, condition);
  }
  isEditQuestionConditionOptionValid(_: State, getters: Getters): (condition: QuestionCondition) => boolean {
    if (getters.editQuestion == null) {
      return () => false;
    }
    const questionsMap = getters.questionsMap;
    return condition => helpers.isQuestionConditionOptionValid(questionsMap, condition);
  }

  questionTypes(): QuestionTypeOption[] {
    return questionTypes;
  }

  previewSurveyData(state: State, getters: Getters, rootState: RootState): ApiSurveyDataPreview {
    return helpers.getSurveyDataPreview(state, rootState);
  }

  flags(state: State, getters: Getters, rootState: RootState) {
    return rootState.featureFlags;
  }
}

export default (GettersDef.prototype as any) as GetterTree<State, any>;
